import { useState } from 'react';
import styles from './Calculator.module.css';

type ExampleKey = 'corn' | 'sunflower' | 'tomato';

interface HeterosisResult {
  p1: number;
  p2: number;
  f1: number;
  mp: number;
  bp: number;
  heterosisMP: number;
  heterobeltiosis: number;
  units: string;
}

const examples: Record<
  ExampleKey,
  { p1: number; p2: number; f1: number; units: string; name: string }
> = {
  corn: {
    p1: 3200,
    p2: 3500,
    f1: 9800,
    units: 'kg/ha',
    name: 'Hibrido Simple de Maiz',
  },
  sunflower: {
    p1: 1800,
    p2: 2100,
    f1: 3400,
    units: 'kg/ha',
    name: 'Girasol Hibrido',
  },
  tomato: {
    p1: 65,
    p2: 72,
    f1: 95,
    units: 'ton/ha',
    name: 'Tomate F1',
  },
};

const format = (value: number, digits = 2): string => value.toFixed(digits);

const getInterpretation = (heterosisMP: number): { title: string; text: string; tone: 'positive' | 'neutral' | 'negative' } => {
  if (heterosisMP >= 50) {
    return {
      tone: 'positive',
      title: 'Heterosis muy alta (>= 50%)',
      text: 'Vigor hibrido excepcional. El F1 supera ampliamente a los progenitores y es muy buen candidato comercial.',
    };
  }
  if (heterosisMP >= 20) {
    return {
      tone: 'positive',
      title: 'Heterosis alta (20% - 50%)',
      text: 'Vigor hibrido significativo y comercialmente util en cultivos donde se explota semilla hibrida.',
    };
  }
  if (heterosisMP >= 5) {
    return {
      tone: 'positive',
      title: 'Heterosis moderada (5% - 20%)',
      text: 'El F1 supera a la media parental, con ganancia moderada dependiente del contexto productivo.',
    };
  }
  if (heterosisMP >= -5) {
    return {
      tone: 'neutral',
      title: 'Sin heterosis significativa (-5% a 5%)',
      text: 'No hay ventaja clara del F1 sobre la media parental. Conviene probar progenitores mas divergentes.',
    };
  }
  return {
    tone: 'negative',
    title: 'Depresion hibrida (< -5%)',
    text: 'El F1 rinde por debajo de la media parental. Esta combinacion no es recomendable para uso comercial.',
  };
};

export const HeterosisEstimator: React.FC = () => {
  const [p1, setP1] = useState<string>('');
  const [p2, setP2] = useState<string>('');
  const [f1, setF1] = useState<string>('');
  const [units, setUnits] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [info, setInfo] = useState<string>('');
  const [result, setResult] = useState<HeterosisResult | null>(null);

  const handleCalculate = (): void => {
    const p1Num = Number(p1);
    const p2Num = Number(p2);
    const f1Num = Number(f1);
    setError('');

    if (Number.isNaN(p1Num) || Number.isNaN(p2Num) || Number.isNaN(f1Num)) {
      setResult(null);
      setError('Complete P1, P2 y F1 con valores numericos validos.');
      return;
    }

    const mp = (p1Num + p2Num) / 2;
    const bp = Math.max(p1Num, p2Num);
    const heterosisMP = mp !== 0 ? ((f1Num - mp) / mp) * 100 : 0;
    const heterobeltiosis = bp !== 0 ? ((f1Num - bp) / bp) * 100 : 0;

    setResult({
      p1: p1Num,
      p2: p2Num,
      f1: f1Num,
      mp,
      bp,
      heterosisMP,
      heterobeltiosis,
      units: units.trim(),
    });
    setInfo('');
  };

  const handleLoadExample = (key: ExampleKey): void => {
    const ex = examples[key];
    setP1(String(ex.p1));
    setP2(String(ex.p2));
    setF1(String(ex.f1));
    setUnits(ex.units);
    setResult(null);
    setError('');
    setInfo(`Ejemplo cargado: ${ex.name}. Presione "Calcular Heterosis".`);
  };

  const maxValue = result ? Math.max(result.p1, result.p2, result.f1, result.mp, result.bp) : 1;
  const interpretation = result ? getInterpretation(result.heterosisMP) : null;

  return (
    <div className={styles.calculatorContainer}>
      <div className={styles.heterosisBox}>
        <div className={styles.calculatorHeader}>
          <h2>Estimador de Heterosis</h2>
          <p>Calcule vigor hibrido comparando F1 con sus progenitores.</p>
        </div>

        <div className={styles.inputSection}>
          <h3>Valores de Progenitores</h3>
          <div className={styles.inputRow}>
            <div className={styles.inputGroup}>
              <label htmlFor="p1">Progenitor 1 (P1)</label>
              <input id="p1" type="number" step="0.1" value={p1} onChange={(event) => setP1(event.target.value)} />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="p2">Progenitor 2 (P2)</label>
              <input id="p2" type="number" step="0.1" value={p2} onChange={(event) => setP2(event.target.value)} />
            </div>
          </div>

          <h3>Valor del Hibrido F1</h3>
          <div className={styles.inputRow}>
            <div className={styles.inputGroup}>
              <label htmlFor="f1">Hibrido F1</label>
              <input id="f1" type="number" step="0.1" value={f1} onChange={(event) => setF1(event.target.value)} />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="units">Unidades</label>
              <input id="units" type="text" value={units} onChange={(event) => setUnits(event.target.value)} placeholder="kg/ha, ton/ha, cm" />
            </div>
          </div>

          <div className={styles.heterosisFormulaCard}>
            <strong>Formulas:</strong>
            <p>MP = (P1 + P2) / 2</p>
            <p>H% = [(F1 - MP) / MP] x 100</p>
            <p>HB% = [(F1 - BP) / BP] x 100</p>
          </div>
        </div>

        <button type="button" className={styles.calculateButton} onClick={handleCalculate}>
          Calcular Heterosis
        </button>

        {error && <div className={styles.errorBox}>{error}</div>}
        {info && <div className={styles.infoBox}>{info}</div>}

        <div className={styles.examplesSection}>
          <h3>Ejemplos rapidos</h3>
          <div className={styles.exampleButtons}>
            <button type="button" className={styles.exampleButton} onClick={() => handleLoadExample('corn')}>
              Hibrido Simple Maiz
            </button>
            <button type="button" className={styles.exampleButton} onClick={() => handleLoadExample('sunflower')}>
              Girasol Hibrido
            </button>
            <button type="button" className={styles.exampleButton} onClick={() => handleLoadExample('tomato')}>
              Tomate F1
            </button>
          </div>
        </div>

        {result && interpretation && (
          <div className={styles.resultsSection}>
            <div className={styles.comparisonBars}>
              <h3>Comparacion de Valores</h3>

              {[
                { label: 'Progenitor 1 (P1)', value: result.p1, className: styles.p1 },
                { label: 'Progenitor 2 (P2)', value: result.p2, className: styles.p2 },
                { label: 'Media Parental (MP)', value: result.mp, className: styles.mp },
                { label: 'Mejor Parental (BP)', value: result.bp, className: styles.bp },
                { label: 'Hibrido F1', value: result.f1, className: styles.f1 },
              ].map((item) => (
                <div key={item.label} className={styles.comparisonItem}>
                  <div className={styles.comparisonLabel}>
                    <span>{item.label}</span>
                    <span>
                      {format(item.value, 1)} {result.units}
                    </span>
                  </div>
                  <div className={styles.comparisonBar}>
                    <div className={`${styles.comparisonFill} ${item.className}`} style={{ width: `${(item.value / maxValue) * 100}%` }}>
                      {format(item.value, 1)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.heterosisResultCard}>
              <h3>Heterosis sobre Media Parental (H%)</h3>
              <div className={`${styles.resultValue} ${result.heterosisMP < 0 ? styles.negative : ''}`}>
                {format(result.heterosisMP)}%
              </div>
              <p className={styles.resultFormula}>
                H% = [({format(result.f1, 1)} - {format(result.mp, 1)}) / {format(result.mp, 1)}] x 100 = {format(result.heterosisMP)}%
              </p>
            </div>

            <div className={styles.heterosisResultCard}>
              <h3>Heterobeltiosis sobre Mejor Parental (HB%)</h3>
              <div className={`${styles.resultValue} ${result.heterobeltiosis < 0 ? styles.negative : ''}`}>
                {format(result.heterobeltiosis)}%
              </div>
              <p className={styles.resultFormula}>
                HB% = [({format(result.f1, 1)} - {format(result.bp, 1)}) / {format(result.bp, 1)}] x 100 = {format(result.heterobeltiosis)}%
              </p>
            </div>

            <div className={`${styles.interpretationBox} ${styles[interpretation.tone]}`}>
              <h4>{interpretation.title}</h4>
              <p>{interpretation.text}</p>
            </div>

            <div className={styles.heterosisTheoryBox}>
              <h4>Bases geneticas de la heterosis</h4>
              <ul>
                <li>Dominancia: complementacion de alelos favorables dominantes.</li>
                <li>Sobredominancia: el heterocigoto supera a ambos homocigotos.</li>
                <li>Epistasis: interacciones inter-locus favorables en el hibrido.</li>
                <li>Aplicacion comercial: maiz, girasol, sorgo y cebolla explotan heterosis.</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
